import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { showSuccess, showError } from '../utils/notifications';
import API_CONFIG from '../config';
import { translateErrorMessage } from '../utils/errorTranslations';

const Login = () => {
  const [credentials, setCredentials] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // L'API attend username/password avec OAuth2PasswordRequestForm
      const formData = new FormData();
      formData.append('username', credentials.email); // FastAPI attend 'username' même si c'est un email
      formData.append('password', credentials.password);
      
      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/login`, {
        method: 'POST',
        body: formData, // FormData pour OAuth2PasswordRequestForm
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Stock du token dans localStorage
        localStorage.setItem('token', data.access_token);
        // Notification de succès
        showSuccess('Connexion réussie!');
        // Redirection vers le dashboard
        navigate('/dashboard');
      } else {
        // Gérer les erreurs de validation (422) et autres erreurs
        let errorMessage = 'Erreur de connexion';
        
        if (data.detail) {
          if (Array.isArray(data.detail)) {
            // Erreurs de validation (422) - extraire le premier message
            const firstError = data.detail[0];
            if (firstError && firstError.msg) {
              errorMessage = firstError.msg;
            }
          } else if (typeof data.detail === 'string') {
            // Erreur simple (string)
            errorMessage = data.detail;
          }
        }
        
        // Traduire et afficher l'erreur
        const translatedError = translateErrorMessage(errorMessage);
        showError(translatedError);
      }
    } catch (error) {
      showError('Erreur réseau, veuillez réessayer');
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 px-4">
      {/* Élément décoratif en haut */}
      <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-primary/10 to-transparent"></div>
      
      <div className="max-w-md w-full space-y-3 z-10" style={{transform: 'translateY(-25%)'}}>
        <div className="flex flex-col items-center">
          {/* Logo pour tous les écrans */}
          <img 
            src="/images/logo-full.png" 
            alt="Mes Prix" 
            className="h-48 sm:h-48 lg:h-48 w-auto mx-auto" 
          />
          <h2 className="mt-1 text-center text-lg sm:text-xl font-extrabold text-gray-900">
            Connexion
          </h2>
          <p className="mt-1 text-center text-sm text-gray-600">
            Retrouvez vos prix de référence et bonnes affaires
          </p>
        </div>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                placeholder="Email"
                value={credentials.email}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">Mot de passe</label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                placeholder="Mot de passe"
                value={credentials.password}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm">
              <Link to="/forgot-password" className="font-medium text-primary hover:text-primary-dark">
                Mot de passe oublié ?
              </Link>
            </div>
            <div className="text-sm">
              <Link to="/register" className="font-medium text-primary hover:text-primary-dark">
                Créer un compte
              </Link>
            </div>
          </div>

          <div>
            <button
              type="submit"
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              disabled={loading}
            >
              {loading ? 'Connexion en cours...' : 'Se connecter'}
            </button>
          </div>
        </form>
      </div>
      
      {/* Footer en bas */}
      <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-gray-100/80 to-transparent p-6">
        <div className="text-center">
          <p className="text-xs text-gray-500 mb-2">© 2025 Mes Prix - Tous droits réservés</p>
          <div className="flex justify-center space-x-4 text-xs text-gray-400">
            <span>Conditions d'utilisation</span>
            <span>•</span>
            <span>Politique de confidentialité</span>
            <span>•</span>
            <span>Support</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 